import React, { useRef, useState, useEffect, Suspense } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import Scene1 from './Scene1'
import Scene2 from './Scene2'
import Scene3 from './Scene3'
import './LabSection.css'

const SCENES = [
  {
    id: 1,
    title: 'Portfolio Title',
    subtitle: 'Miroirs & Fresnel',
    description: 'Texte 3D reflété dans des miroirs animés. Effet thin-film fresnel sur les surfaces réfléchissantes avec cube camera en temps réel.',
    tech: ['Three.js', 'React Three Fiber', 'ThinFilmFresnelMap', 'CubeCamera'],
    color: '#00ff88',
    accent: '#00aaff',
    component: Scene1,
    camera: { position: [0, 0, 15], fov: 70 },
  },
  {
    id: 2,
    title: 'Web Design',
    subtitle: 'Diamants & Métal',
    description: 'Diamants 3D en orbite avec matériaux physiques (roughness/metalness). Réflexions env-map temps réel sur fond violet.',
    tech: ['Three.js', 'MeshPhysical', 'MatcapTexture', 'CubeCamera'],
    color: '#14CEFF',
    accent: '#a78bfa',
    component: Scene2,
    camera: { position: [0, 0, 12], fov: 70 },
  },
  {
    id: 3,
    title: 'Développeur WEB',
    subtitle: 'Physics & Cubes',
    description: 'Simulation physique réelle avec cannon.js. Cubes réfléchissants cliquables qui s\'envolent. Ombres dynamiques.',
    tech: ['cannon.js', 'Physics', 'React Three Fiber', 'Shadows'],
    color: '#f70131',
    accent: '#ff8c00',
    component: Scene3,
    camera: { position: [0, 5, 20], fov: 75 },
    hint: '👆 Cliquez sur les cubes !',
  },
]

function useReveal(threshold) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect() }
    }, { threshold: threshold || 0.1 })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible]
}

function SceneCard({ scene, index, visible }) {
  const [active, setActive] = useState(false)
  const SceneComponent = scene.component

  return (
    <div
      className={"scene-card glass-card" + (visible ? " revealed" : "")}
      style={{ "--delay": (index * 150) + "ms", "--scene-color": scene.color, "--scene-accent": scene.accent }}
    >
      <div
        className={"scene-canvas-wrap" + (active ? " active" : "")}
        onClick={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
      >
        {!active && (
          <div className="scene-canvas-overlay">
            <div className="scene-play-btn">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M6 4l12 6-12 6V4z" fill="currentColor" />
              </svg>
              Interagir
            </div>
            {scene.hint && <div className="scene-hint">{scene.hint}</div>}
          </div>
        )}
        <Canvas
          camera={scene.camera}
          gl={{ antialias: true, alpha: false }}
          shadows={scene.id === 3}
          style={{ background: scene.id === 3 ? '#1a0000' : '#080808' }}
        >
          <Suspense fallback={null}>
            <SceneComponent />
          </Suspense>
          {active && <OrbitControls enableZoom={false} enablePan={false} />}
        </Canvas>
      </div>

      <div className="scene-content">
        <div className="scene-header">
          <div>
            <p className="scene-subtitle">{scene.subtitle}</p>
            <h3 className="scene-title">{scene.title}</h3>
          </div>
          <span className="scene-id-badge">Scene {scene.id}</span>
        </div>
        <p className="scene-desc">{scene.description}</p>
        <div className="scene-tech">
          {scene.tech.map(function(t) {
            return <span key={t} className="scene-tech-tag">{t}</span>
          })}
        </div>
      </div>

      <div className="scene-border-line" />
    </div>
  )
}

export default function LabSection() {
  const [sectionRef, visible] = useReveal()

  return (
    <section id="lab" className="section lab-section" ref={sectionRef}>
      <div className={"section-inner" + (visible ? " visible" : "")}>
        <div className="section-label"><span>05</span> Lab 3D</div>
        <h2 className="section-title">
          Expériences <span className="gradient-text">3D</span>
        </h2>
        <p className="section-desc">
          Trois scènes interactives créées avec Three.js et React Three Fiber —
          rendu temps réel, physique et effets visuels avancés.
        </p>

        <div className="lab-banner glass-card">
          <span className="lab-banner-icon">✦</span>
          <p>
            <strong>Cliquez sur une scène</strong> pour l'activer et interagir.
            La scène 3 répond aux clics sur les cubes !
          </p>
        </div>

        <div className="scenes-grid">
          {SCENES.map(function(scene, i) {
            return <SceneCard key={scene.id} scene={scene} index={i} visible={visible} />
          })}
        </div>
      </div>
    </section>
  )
}
